import type { EventBatchResponse, EventIn } from '@nelnela/contracts';
import { api, ApiError } from './api';

/**
 * Offline-safe event queue (04-flows "Session & activity lifecycle"): the client only reports
 * behavior; the server dedupes by client ULID, so anything may be re-sent. Invariants:
 *   - FIFO: events leave in enqueue order (session → activity → events replay order).
 *   - An event is dropped only when the server names it (accepted | duplicate | rejected) or
 *     when the queue overflows MAX_QUEUE (oldest first).
 *   - Ids the server does not mention (202: FK not landed yet) stay and are re-sent.
 *   - Any transport/HTTP failure keeps the whole batch and backs off; success resets backoff.
 *   - Storage mirrors the in-memory queue after every change; a new queue on the same storage
 *     resumes it. Corrupt storage starts empty (never throws at construction).
 * Nothing here emits `lookup` (written server-side by gloss) or `activity_started/completed`
 * (written server-side by activityStart/activityEnd).
 */

export const EVENTS_STORAGE_KEY = 'nn.events.v1';
export const MAX_QUEUE = 5000;
/** A 202-deferred event is re-sent at most this many times before it is dropped: its session or
 *  activity never landed (a page whose sessionStart never succeeded), so it never will. */
export const MAX_DEFERRALS = 40;
const DEFAULT_FLUSH_MS = 3000;
const DEFAULT_MAX_BATCH = 200;
const MAX_BACKOFF_MS = 60_000;

export interface EventStorage {
  get(): string | null;
  set(v: string): void;
}

export interface SendResult {
  status: number;
  body: EventBatchResponse;
}

export interface EventQueueOptions {
  /** `keepalive` is set on the best-effort pagehide/hidden flush. */
  send: (events: EventIn[], opts?: { keepalive?: boolean }) => Promise<SendResult>;
  /** Awaited before every send: the study session must have landed (sessionStart's promise). */
  gate?: () => Promise<void>;
  flushIntervalMs?: number;
  maxBatch?: number;
  /** null = memory only; undefined = localStorage under EVENTS_STORAGE_KEY when available. */
  storage?: EventStorage | null;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

function defaultStorage(): EventStorage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const ls = window.localStorage;
    return {
      get: () => ls.getItem(EVENTS_STORAGE_KEY),
      set: (v) => ls.setItem(EVENTS_STORAGE_KEY, v),
    };
  } catch {
    return null; // storage access can throw (Safari private mode, sandboxed iframes)
  }
}

/** 4xx that no retry can fix. 401/408/409/429 and 5xx/network are transient. */
function isPermanentRejection(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  const st = err.problem.status;
  return st >= 400 && st < 500 && ![401, 408, 409, 429].includes(st);
}

function looksLikeEvent(x: unknown): x is EventIn {
  return (
    typeof x === 'object' &&
    x !== null &&
    typeof (x as { id?: unknown }).id === 'string' &&
    typeof (x as { eventType?: unknown }).eventType === 'string'
  );
}

export class EventQueue {
  private queue: EventIn[] = [];
  private readonly send: EventQueueOptions['send'];
  private readonly gate: (() => Promise<void>) | undefined;
  private readonly flushIntervalMs: number;
  private readonly maxBatch: number;
  private readonly storage: EventStorage | null;
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  private timer: unknown = null;
  private inflight: Promise<void> | null = null;
  private failures = 0;
  private stopped = false;
  private listening = false;
  private lastFlushAt: number | null = null;
  private lastError: unknown = null;
  /** id → times the server left it unmentioned (202); not persisted — a reload starts it over */
  private deferrals = new Map<string, number>();

  constructor(opts: EventQueueOptions) {
    this.send = opts.send;
    this.gate = opts.gate;
    this.flushIntervalMs = opts.flushIntervalMs ?? DEFAULT_FLUSH_MS;
    this.maxBatch = opts.maxBatch ?? DEFAULT_MAX_BATCH;
    this.storage = opts.storage === undefined ? defaultStorage() : opts.storage;
    this.now = opts.now ?? Date.now;
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
    this.load();
  }

  /** Append, persist, and make sure a flush is coming. */
  enqueue(e: EventIn): void {
    this.queue.push(e);
    this.trim();
    this.persist();
    if (!this.stopped) this.schedule(this.flushIntervalMs);
  }

  /**
   * Send one batch. No-op when empty; a concurrent call joins the in-flight flush rather than
   * starting a second (order + idempotency depend on one sender at a time).
   */
  flush(opts?: { keepalive?: boolean }): Promise<void> {
    if (this.inflight) return this.inflight;
    if (this.queue.length === 0) return Promise.resolve();
    this.inflight = this.doFlush(opts).finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  /** Attach page-lifecycle flushes and pick up whatever storage held from an earlier load. */
  start(): void {
    this.stopped = false;
    if (!this.listening && typeof window !== 'undefined' && typeof document !== 'undefined') {
      window.addEventListener('pagehide', this.onHide);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.listening = true;
    }
    if (this.queue.length > 0) this.schedule(this.flushIntervalMs);
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
    if (this.listening) {
      window.removeEventListener('pagehide', this.onHide);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.listening = false;
    }
  }

  size(): number {
    return this.queue.length;
  }

  /** Copy, oldest first (tests / debug). */
  peek(): EventIn[] {
    return this.queue.slice();
  }

  stats(): { size: number; failures: number; lastFlushAt: number | null; lastError: unknown } {
    return {
      size: this.queue.length,
      failures: this.failures,
      lastFlushAt: this.lastFlushAt,
      lastError: this.lastError,
    };
  }

  // --- internals ---

  private onHide = (): void => {
    // Best effort: keepalive lets the request outlive the page. Whatever does not make it stays
    // persisted and is re-sent on the next load (server dedupes by ULID).
    void this.flush({ keepalive: true });
  };

  private onVisibility = (): void => {
    if (document.visibilityState === 'hidden') void this.flush({ keepalive: true });
  };

  private schedule(ms: number): void {
    if (this.timer !== null) return; // one pending flush at a time
    this.timer = this.setTimer(() => {
      this.timer = null;
      void this.flush();
    }, ms);
  }

  private backoffMs(): number {
    // failures=1 → base, 2 → 2×base, 3 → 4×base … capped.
    return Math.min(this.flushIntervalMs * 2 ** (this.failures - 1), MAX_BACKOFF_MS);
  }

  private async doFlush(opts?: { keepalive?: boolean }): Promise<void> {
    let batch: EventIn[] = [];
    try {
      if (this.gate) await this.gate();
      batch = this.queue.slice(0, this.bisect ?? this.maxBatch);
      this.bisect = null;
      const { status, body } = await this.send(batch, opts);
      const done = new Set<string>([...body.accepted, ...body.duplicates]);
      for (const r of body.rejected) {
        // Rejected = the server will never take it (validation / final DB error). Dropping is the
        // only option: events are append-only and never edited client-side.
        console.warn(`[events] rejected ${r.id}: ${r.reason}`);
        done.add(r.id);
      }
      if (status === 202) {
        // Unmentioned ids are deferred (their session/activity row hasn't landed). Count and give
        // up after MAX_DEFERRALS so a session that never lands can't sit at the FIFO head forever.
        for (const e of batch) {
          if (done.has(e.id)) continue;
          const n = (this.deferrals.get(e.id) ?? 0) + 1;
          if (n >= MAX_DEFERRALS) {
            console.warn(
              `[events] dropping ${e.id}: deferred ${n}× (session/activity never landed)`,
            );
            done.add(e.id);
            this.deferrals.delete(e.id);
          } else this.deferrals.set(e.id, n);
        }
      }
      if (done.size > 0) {
        this.queue = this.queue.filter((e) => !done.has(e.id));
        for (const id of done) this.deferrals.delete(id);
      }
      this.failures = 0;
      this.lastError = null;
      this.lastFlushAt = this.now();
      this.persist();
      // 202 leftovers (and anything beyond maxBatch) go next tick at the base interval.
      if (this.queue.length > 0 && !this.stopped) this.schedule(this.flushIntervalMs);
    } catch (err) {
      this.lastError = err;
      if (isPermanentRejection(err)) {
        // The whole request was refused for good (400/413/422 …): retrying the same head batch
        // would wedge the queue. Isolate the poison by bisection — halve until one event is left,
        // then drop it — so its neighbours still land. (The server validates per event and 422s
        // only on a broken envelope, so this is a last line of defence, not the normal path.)
        const half = Math.max(1, Math.floor(batch.length / 2));
        if (batch.length <= 1) {
          const bad = batch[0];
          if (bad) {
            console.warn(`[events] dropping ${bad.id}: server refused it`, err);
            this.queue = this.queue.filter((e) => e.id !== bad.id);
            this.persist();
          }
          this.bisect = null;
        } else {
          this.bisect = half;
        }
        this.failures = 0;
        if (this.queue.length > 0 && !this.stopped) this.schedule(this.flushIntervalMs);
        return;
      }
      this.failures++;
      console.warn(
        `[events] flush failed (attempt ${this.failures}), keeping ${this.queue.length}`,
        err,
      );
      if (!this.stopped) this.schedule(this.backoffMs());
    }
  }

  /** When set, the next batch is capped at this size (bisecting a permanently refused batch). */
  private bisect: number | null = null;

  private trim(): void {
    if (this.queue.length <= MAX_QUEUE) return;
    const drop = this.queue.length - MAX_QUEUE;
    this.queue.splice(0, drop);
    console.warn(`[events] queue over ${MAX_QUEUE}; dropped ${drop} oldest`);
  }

  private load(): void {
    if (!this.storage) return;
    try {
      const raw = this.storage.get();
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      this.queue = parsed.filter(looksLikeEvent);
      this.trim();
    } catch {
      this.queue = []; // corrupt storage: start empty rather than wedge the app
    }
  }

  private persist(): void {
    if (!this.storage) return;
    try {
      this.storage.set(JSON.stringify(this.queue));
    } catch (err) {
      console.warn('[events] persist failed', err); // quota etc.; the memory copy still flushes
    }
  }
}

/** The app's queue: eventsBatch over the API client, gated on the study session having landed. */
export function createDefaultEventQueue(gate?: () => Promise<void>): EventQueue {
  return new EventQueue({
    send: (events, opts) => api.eventsBatch({ events }, opts),
    gate,
  });
}
